import { useEffect, useRef } from "react";
import type { PanelEntry, PanelPresentation } from "../lib/api";
import { useDraft } from "../lib/drafts";
import { declaredIcon, Icon } from "../components/Icon";
import { PanelView } from "./PanelView";
import type { VisiblePanel } from "./MainPanelPresence";

/**
 * A page the Agent asked to show beside this conversation (`ui_presentations`).
 *
 * A `card` or `sidebar` request is one row: the page's title and 打开, which
 * the Owner can also close. The page goes on the main stage only when the
 * Owner presses 打开 (`onOpen`), so the conversation keeps its height on every
 * screen. A `dialog` request opens over the page and can be put away.
 */
export function PresentationShelf({ requests, panels, sessionId, seatKnown, onToast, currentPanels = [], onOpen }: {
  requests: PanelPresentation[]; panels: PanelEntry[]; sessionId: string;
  seatKnown: boolean; onToast: (message: string, tone: "good" | "critical") => void;
  currentPanels?: VisiblePanel[];
  /** The Owner's press on a row: put that page on the main stage. */
  onOpen: (request: PanelPresentation) => void;
}) {
  const [history, setHistory, storageError] = useDraft<{ latest: string; createdAt: number; collapsed: boolean }>(
    `presentations:${sessionId}`, { latest: "", createdAt: -1, collapsed: false });
  const current = requests.filter(request => seatKnown && request.session_id === sessionId
    && panels.some(panel => panel.key === request.panel_key && panel.audience === "owner" && panel.generation_id === request.panel_generation))
    .sort((a, b) => a.created_at - b.created_at || a.key.localeCompare(b.key)).at(-1);
  const unseen = Boolean(current && current.created_at > history.createdAt);
  useEffect(() => {
    if (current && unseen) setHistory({ latest: current.request_id, createdAt: current.created_at, collapsed: false });
  }, [current?.request_id, unseen, setHistory]);
  // A replay of an older request may not replace the one the Owner most
  // recently received. A request never changes the route or mounts another
  // composer: only the Owner's own press moves the page.
  if (!current || (!unseen && history.latest !== current.request_id)) return null;
  if (currentPanels.some(panel => {
    if (panel.key !== current.panel_key || panel.generation_id !== current.panel_generation) return false;
    const context = panel.context ?? {};
    const visibleContext: Record<string, string> = { ...context, session: context.session ?? context.session_id ?? sessionId,
      session_id: context.session_id ?? context.session ?? sessionId };
    return Object.keys(visibleContext).length === Object.keys(current.context).length
      && Object.entries(current.context).every(([key, value]) => visibleContext[key] === value);
  })) return null;
  if (current.placement !== "dialog") {
    // Closed, the row stays away for this request; a newer one brings it back.
    if (!unseen && history.collapsed) return null;
    const target = panels.find(panel => panel.key === current.panel_key);
    return <section className="presentation-row" aria-label={current.title}>
      <Icon name={declaredIcon(target?.icon)} size={18} className="presentation-row-icon" />
      <span className="presentation-row-title" data-quoted="true"><span className="micro">上次展示 · </span>{current.title}</span>
      <button className="btn" type="button" aria-label={`${current.title}，打开`} onClick={() => onOpen(current)}>打开</button>
      <button className="btn btn-quiet presentation-row-close" type="button" aria-label="关闭" title="关闭"
        onClick={() => setHistory({ latest: current.request_id, createdAt: current.created_at, collapsed: true })}>
        <Icon name="no" size={16} />
      </button>
    </section>;
  }
  return <PresentationDialog key={current.request_id} request={current} panels={panels} onToast={onToast} seatKnown={seatKnown}
    collapsed={unseen ? false : history.collapsed} storageError={storageError}
    setCollapsed={collapsed => setHistory({ latest: current.request_id, createdAt: current.created_at, collapsed })} />;
}

function PresentationDialog({ request, panels, onToast, seatKnown, collapsed, setCollapsed, storageError }: {
  request: PanelPresentation; panels: PanelEntry[]; seatKnown: boolean;
  onToast: (message: string, tone: "good" | "critical") => void;
  collapsed: boolean; setCollapsed: (collapsed: boolean) => void; storageError: string | null;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const opened = useRef(!collapsed);
  if (!collapsed) opened.current = true;
  useEffect(() => {
    if (!dialog.current) return;
    if (collapsed) dialog.current.close();
    else if (!dialog.current.open) dialog.current.showModal();
  }, [collapsed]);
  const controls = <button className="btn btn-quiet" type="button" onClick={() => setCollapsed(!collapsed)}>
    {collapsed ? `展开界面：${request.title}` : "收起界面，继续对话"}
  </button>;
  return <>{collapsed ? controls : null}
    <dialog ref={dialog} className="interaction-dialog" aria-label={request.title}
      onCancel={event => { event.preventDefault(); setCollapsed(true); }}>
      {storageError ? <p role="status">{storageError}</p> : null}{controls}
      <div hidden={collapsed}>{opened.current ? <PanelView key={`${request.panel_key}:${request.panel_generation}`} panelKey={request.panel_key}
        panels={panels} context={request.context} heading="h2" seatKnown={seatKnown} onToast={onToast} presentation="embedded" /> : null}</div>
    </dialog></>;
}
